import { CircularProgress } from '@material-ui/core';
import Header from 'components/Headers/Header';
import ProdutoModel from 'models/Produto';
import ProdutoAtividade from 'models/ProdutoAtividade';
import ProdutoCategoria from 'models/ProdutoCategoria';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FaEdit, FaPlusCircle, FaTrash } from 'react-icons/fa';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  Modal,
  Row,
  Table,
} from 'reactstrap';
import api from 'services/api';
import Swal from 'sweetalert2';

type LocationState = {
  produto?: ProdutoModel;
};

interface PrdAtiv extends ProdutoAtividade {
  produto: {
    ProdutoCategoria: {
      subprojeto: string;
    };
  };
}

const Atividades: React.FC = () => {
  const location = useLocation<LocationState>();
  const { produto } = location.state;

  const [prodAtivId, setProdAtivId] = useState<number | null>(null);

  const [formModalState, setFormModalState] = useState(false);
  const [codigoSGE, setCodigoSGE] = useState('');
  const [codigoSAP, setCodigoSAP] = useState('');
  const [significado, setSignificado] = useState('');
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const { data: queryProdutoCategoria, isLoading } = useQuery<ProdutoCategoria>(
    ['produto-atividade', produto?.id],
    async () => {
      if (!produto) return null;
      const result = await api.get(`/produto-categoria/produto/${produto.id}`);
      return result.data;
    },
    {
      enabled: !!produto,
    },
  );

  const { data: queryProdutoAtividade, isLoading: isLoadingProdAtiv } =
    useQuery<PrdAtiv[]>(['produto-atividade'], async () => {
      if (!produto) return null;
      const result = await api.get(`/produto-atividade/${produto.id}`);
      return result.data;
    });

  async function handleInsert() {
    setFormModalState(!formModalState);
  }

  function limparCampos() {
    setCodigoSAP('');
    setCodigoSGE('');
    setSignificado('');
    setProdAtivId(null);
  }

  async function handleSave() {
    if (codigoSAP === '' && codigoSGE === '') {
      toast.error('É necessário informar o código SAP e/ou código SGE.');
      return;
    }

    if (!queryProdutoCategoria) {
      toast.error('Este produto não tem uma categoria');
    }
    setIsSaving(true);

    const toastId = toast.loading('Salvando...');

    try {
      if (prodAtivId) {
        await api.put(`/produto-atividade/`, {
          id: prodAtivId,
          codSAP: codigoSAP,
          codSGE: codigoSGE,
          significado,
        });
      } else {
        await api.post(`/produto-atividade/`, {
          codSAP: codigoSAP,
          codSGE: codigoSGE,
          significado,
          produtoId: produto?.id ?? null,
        });
      }

      toast.success('Registro salvo');
    } finally {
      toast.dismiss(toastId);
    }
    setIsSaving(false);

    queryClient.invalidateQueries({ queryKey: ['produto-atividade'] });

    limparCampos();
    setFormModalState(!formModalState);
  }

  function handleUpdate(prdAtiv: PrdAtiv) {
    setProdAtivId(prdAtiv.id);
    setCodigoSAP(prdAtiv?.codSAP ?? '');
    setCodigoSGE(prdAtiv?.codSGE ?? '');
    setSignificado(prdAtiv?.significado ?? '');
    setFormModalState(!formModalState);
  }

  function mostrarSubProjTitulo() {
    let titulo = '';
    if (queryProdutoCategoria) {
      titulo += queryProdutoCategoria?.descricao;

      if (queryProdutoCategoria?.subprojeto) {
        titulo += `(${queryProdutoCategoria?.subprojeto}) `;
      }

      if (queryProdutoCategoria?.significado) {
        titulo += `- ${queryProdutoCategoria?.significado}`;
      }
    }
    return titulo;
  }

  async function handleDelete(id: number | null) {
    if (id) {
      Swal.fire({
        title: `Deseja excluir o Registro?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `Sim`,
        confirmButtonColor: '#d33',
        cancelButtonText: `Não`,
      }).then(async result => {
        if (result.isConfirmed) {
          const toastId = toast.loading('Excluindo');
          setIsSaving(true);
          try {
            await api.delete(`/produto-atividade/${id}`);
            queryClient.invalidateQueries({ queryKey: ['produto-atividade'] });
            Swal.close();
            toast.success('Registro excluído');
          } finally {
            setIsSaving(false);
            toast.dismiss(toastId);
          }
        }
      });
    } else {
      toast.error('erro ao excluir registro!');
    }
  }

  return (
    <>
      <Header showCards={false} />
      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <FormGroup className="mb-4">
              <InputGroup>
                <Button
                  className="btn-icon btn-2"
                  color="primary"
                  type="button"
                  disabled={isLoadingProdAtiv}
                  onClick={handleInsert}
                >
                  <FaPlusCircle />
                </Button>
              </InputGroup>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <div className="col">
            <Modal
              className="modal-dialog-centered"
              size="sm"
              isOpen={formModalState}
              toggle={() => {
                setFormModalState(!formModalState);
                limparCampos();
              }}
            >
              <div className="modal-body p-0">
                <Card className="bg-secondary shadow border-0">
                  <CardBody className="px-lg-5 py-lg-5">
                    <Form role="form">
                      <FormGroup className="mb-3">
                        <small>Código SGE</small>
                        <InputGroup className="input-group-alternative">
                          <Input
                            placeholder="Código SGE"
                            value={codigoSGE}
                            type="text"
                            onChange={e => {
                              setCodigoSGE(e.target.value);
                            }}
                          />
                        </InputGroup>
                      </FormGroup>
                      <FormGroup className="mb-3">
                        <small>Código SAP</small>
                        <InputGroup className="input-group-alternative">
                          <Input
                            placeholder="Código SAP"
                            value={codigoSAP}
                            type="text"
                            onChange={e => {
                              setCodigoSAP(e.target.value);
                            }}
                          />
                        </InputGroup>
                      </FormGroup>
                      <FormGroup className="mb-3">
                        <small>Significado</small>
                        <InputGroup className="input-group-alternative">
                          <Input
                            placeholder="Significado"
                            value={significado}
                            type="text"
                            onChange={e => {
                              setSignificado(e.target.value);
                            }}
                          />
                        </InputGroup>
                      </FormGroup>

                      <div className="text-center">
                        <Button
                          className="my-4"
                          color="primary"
                          type="button"
                          disabled={isLoadingProdAtiv || isSaving}
                          onClick={handleSave}
                        >
                          Salvar
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </div>
            </Modal>

            <Card className="shadow">
              <CardHeader className="border-0">
                <Row>
                  <Col sm="9">
                    <h3 className="mb-0">
                      Atividades do Produto <i>{produto?.nome ?? ''}</i>
                    </h3>
                    <small>{mostrarSubProjTitulo()}</small>
                  </Col>
                </Row>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th>Código SGE</th>
                    <th>Código SAP</th>
                    <th>Significado</th>
                    <th> </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingProdAtiv && (
                    <CircularProgress
                      className="mt-2 ml-2"
                      color="inherit"
                      size={10}
                    />
                  )}
                  {!isLoadingProdAtiv &&
                    queryProdutoAtividade &&
                    queryProdutoAtividade.map((prdAtiv: PrdAtiv) => {
                      const { produto: prod } = prdAtiv;
                      return (
                        <tr key={prdAtiv.id}>
                          <td className="py-2">{prdAtiv.codSGE}</td>
                          <td className="py-2">{prdAtiv.codSAP}</td>
                          <td className="py-2">{prdAtiv.significado}</td>
                          <td className="py-2 text-center">
                            <div className="float-right">
                              <Button
                                size="sm"
                                className="btn-icon"
                                color="warning"
                                type="button"
                                disabled={isLoadingProdAtiv}
                                onClick={() => {
                                  handleUpdate(prdAtiv);
                                }}
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                size="sm"
                                className="btn-icon"
                                color="danger"
                                type="button"
                                disabled={isLoadingProdAtiv}
                                onClick={() => {
                                  handleDelete(prdAtiv?.id ?? null);
                                }}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default Atividades;
